import React, { useEffect, useMemo, useState } from 'react';

import { Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import AuditModal from './AuditModal';
import { buildColumns } from './columns';
import ImportModal from './ImportModal';
import {
  createSiteListRow,
  deleteSiteListRow,
  importSiteList,
  listLineOfBusiness,
  listSiteList,
  updateSiteListRow,
} from './operations';
import SiteListForm from './SiteListForm';
import { knownValuesByField, toCsvData, toLobOptions } from './utils';
import {
  Breadcrumbs,
  Button,
  ExportCsvButton,
  Footer,
  SubHeader,
  TableFilter,
  TablePagination,
} from '../../components';
import { newDateFormatted } from '../../utils';

import './styles.scoped.css';

/**
 * CP-857: SiteList maintenance for the BI Admin role.
 *
 * SiteList (branch -> Recruiter/RRM/AM/Area/LOB/Cost Center) is the branch reference table
 * behind the Centerline Weekly Reports and the new PRST Operations BI. Until now it was
 * maintained by IT ticket from a spreadsheet — slow, and the source of the whitespace and
 * orphan-branch faults that silently break the Power BI joins. This screen replaces that
 * cycle; all validation is enforced server-side in the stored procedures.
 *
 * Brand is implicit: this build talks to its own brand's API and database. Centerline sees
 * TLC's list (323 rows, Branch x LOB), PRST sees its own (empty until seeded, every row
 * LOB = 'All').
 *
 * Local state only — this is a maintenance/search screen, so no Redux slice (house rule).
 */
const SiteList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [lobOptions, setLobOptions] = useState([]);
  const [lobLoading, setLobLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [auditScope, setAuditScope] = useState(undefined);
  const [auditOpen, setAuditOpen] = useState(false);

  const refresh = () => {
    setLoading(true);
    setError('');
    return listSiteList()
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    listLineOfBusiness()
      .then(list => setLobOptions(toLobOptions(list)))
      .finally(() => setLobLoading(false));
  }, []);

  const columns = useMemo(
    () =>
      buildColumns({
        onEdit: setEditing,
        onHistory: scope => {
          setAuditScope(scope);
          setAuditOpen(true);
        },
      }),
    [],
  );

  const tableInstance = useTable(
    {
      columns,
      data: rows,
      // sortBy entries need an explicit desc; react-table treats a missing one as undefined
      // rather than false. Default view is grouped by LOB, then branch number ascending.
      initialState: {
        sortBy: [
          { id: 'LOB', desc: false },
          { id: 'BranchNumber', desc: false },
        ],
        pageSize: 25,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow, setFilter, setGlobalFilter } =
    tableInstance;

  // TableFilter keys its dropdown by `Header` and reserves the empty key for its own "All"
  // option, so the action column (no Header, no accessor) must never reach it: selecting
  // "All" would otherwise match that column and call setFilter(undefined, ...), which
  // throws "Could not find a column with id: undefined". Filtered here as well as in
  // TableFilter so the screen stays correct regardless of that component's internals.
  const filterableColumns = useMemo(() => columns.filter(column => column.Header && column.accessor), [columns]);

  const filter = (event, columnName) => {
    const filterColumn = filterableColumns.find(column => column.Header === columnName);
    if (filterColumn) {
      setFilter(filterColumn.accessor, event.target.value);
    } else {
      setGlobalFilter(event.target.value);
    }
  };

  // Each handler resolves only after the grid has been refreshed, so the dialog stays open
  // (and keeps showing its spinner) until the new state is on screen. Rejections propagate
  // to the dialog, which renders the server's message.
  const handleCreate = async row => {
    await createSiteListRow(row);
    setAdding(false);
    setNotice(`Branch ${row.BranchNumber} / ${row.LOB} added.`);
    await refresh();
  };

  const handleUpdate = async row => {
    await updateSiteListRow(row, { BranchNumber: editing.BranchNumber, LOB: editing.LOB });
    setEditing(null);
    setNotice(`Branch ${row.BranchNumber} / ${row.LOB} saved.`);
    await refresh();
  };

  const handleDelete = async () => {
    await deleteSiteListRow({ BranchNumber: editing.BranchNumber, LOB: editing.LOB });
    const deleted = `Branch ${editing.BranchNumber} / ${editing.LOB}`;
    setEditing(null);
    setNotice(`${deleted} deleted. Its previous values are in the change history.`);
    await refresh();
  };

  const handleImport = async ({ mode, rows: importRows }) => {
    const result = await importSiteList({ mode, rows: importRows });
    setImporting(false);
    setNotice(result?.Message || 'Import complete.');
    await refresh();
  };

  const fileName = useMemo(() => `sitelist-${newDateFormatted.toString()}.csv`, []);

  // The people values already in use, for the form's "new value" warning. Derived from the
  // rows already on screen, so it costs no extra request.
  const knownValues = useMemo(() => knownValuesByField(rows), [rows]);

  return (
    <>
      <SubHeader
        text="Admin Portal"
        buttons={[{ icon: <Home className="subheader-icon" />, onPress: () => navigate(-1) }]}
      />
      <Breadcrumbs leftTitle="Admin Portal" rightTitle="SiteList" route="/admin-portal" />

      <div className="wrap">
        <div className="toolbar">
          <div className="toolbar-actions">
            <Button variant="primary" text="Add Row" onPress={() => setAdding(true)} disabled={loading} />
            <Button variant="secondary" text="Import" onPress={() => setImporting(true)} disabled={loading} />
            <Button
              variant="outline"
              text="Change History"
              onPress={() => {
                setAuditScope(undefined);
                setAuditOpen(true);
              }}
            />
            <ExportCsvButton data={toCsvData(rows)} filename={fileName} />
          </div>
          <p className="row-count">{loading ? 'Loading…' : `${rows.length} row${rows.length === 1 ? '' : 's'}`}</p>
        </div>

        {/* CP-857 requirement: SiteList is a Type-1 current-state table with no effective
            dating, so edits are not scheduled -- they reach the reports at the next refresh.
            Say so, rather than leaving users to guess why a change is not visible yet. */}
        <p className="screen-note">
          Changes take effect in BI at the next dataset refresh. SiteList holds current state only — there is no
          effective-dating.
        </p>

        {!!notice && <p className="screen-notice">{notice}</p>}
        {!!error && <p className="form-error">{error}</p>}

        {!loading && !error && (
          <>
            <div className="table-controls">
              <TableFilter columns={filterableColumns} onSearchChange={filter} />
              <TablePagination tableInstance={tableInstance} />
            </div>

            <div className="table-frame">
              {rows.length ? (
                <table className="data-table" {...getTableProps()}>
                  <thead>
                    {headerGroups.map(headerGroup => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {/* An abbreviated column (DBD, AM, SL, RRM, RM, LOB) carries its full name as
                            `hint`; show it as the header's tooltip, replacing react-table's
                            own "Toggle SortBy" title, and mark the header so it reads as
                            hoverable. */}
                        {headerGroup.headers.map(column => (
                          <th
                            {...column.getHeaderProps(
                              column.getSortByToggleProps({
                                title: column.hint || 'Sort by this column',
                                className: column.hint ? 'has-hint' : undefined,
                              }),
                            )}>
                            {column.render('Header')}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map(row => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map(cell => (
                            <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                // PRST's table is empty until it is seeded from the Service Center Bible,
                // so this is a first-run state, not an error.
                <p className="table-empty">
                  No SiteList rows yet. Use Import to load the list, or Add Row to start one.
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {(adding || !!editing) && (
        <SiteListForm
          row={editing || {}}
          isEdit={!!editing}
          lobOptions={lobOptions}
          lobLoading={lobLoading}
          knownValues={knownValues}
          onSave={editing ? handleUpdate : handleCreate}
          onDelete={handleDelete}
          onCancel={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      )}

      {importing && <ImportModal onImport={handleImport} onCancel={() => setImporting(false)} />}

      {auditOpen && <AuditModal scope={auditScope} onClose={() => setAuditOpen(false)} />}

      <Footer />
    </>
  );
};

export default SiteList;
